// Features: MCRO001-F0022, MCRO001-F0023
// CI tests: T-MCRO-022, T-MCRO-023
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { choreographyApi, organisationApi, userApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { CommunityReviewLayer, COMMUNITY_REVIEW_DISPLAYS, normalizeCommunityReviewDisplay, readCommunityReviewDisplay, writeCommunityReviewDisplay } from './CommunityReview';
import './MusicChoreography.css';

const payload = (response) => response?.data?.data || response?.data || {};
const unavailable = 'This Sheet review is not available for this account. Contact the Sheet owner if you think this is a mistake.';

function organisationIdOf(organisation = {}) {
  // Join-request records have their own `id`; prefer their explicit target
  // organisation before falling back to a directory organisation's `id`.
  return String(organisation?.organisationId ?? organisation?.organisation_id ?? organisation?.id ?? organisation?.uuid ?? '');
}

function listFrom(response, keys = []) {
  const data = payload(response);
  if (Array.isArray(data)) return data;
  for (const key of keys) if (Array.isArray(data?.[key])) return data[key];
  return [];
}

function organisationNameOf(organisation = {}) {
  return String(organisation?.name ?? organisation?.organisationName ?? organisation?.organisation_name ?? '').trim();
}

// MCRO001-F0022 / T-MCRO-022 cases I-J: this component is shown only after
// the protected exact-Sheet projection has loaded. Membership and pending state
// come from their own authenticated APIs, while the public directory supplies
// the display name. None of those calls can change or preserve Sheet authority.
export function OptionalStudioJoin({ organisationId }) {
  const [state, setState] = useState('loading');
  const [studioName, setStudioName] = useState('');
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const statusRef = useRef(null);
  const mountedRef = useRef(true);
  const authorityGenerationRef = useRef(0);

  // React StrictMode intentionally replays mount effects in development.
  // Re-arm the guard on every effect setup so that replayed live requests are
  // not mistaken for post-unmount completions.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      authorityGenerationRef.current += 1;
    };
  }, []);

  const focusStatus = useCallback(() => {
    window.requestAnimationFrame(() => statusRef.current?.focus());
  }, []);

  const loadAuthority = useCallback(async () => {
    const generation = authorityGenerationRef.current + 1;
    authorityGenerationRef.current = generation;
    const isCurrent = () => mountedRef.current && authorityGenerationRef.current === generation;
    if (!isCurrent()) return 'stale';
    if (!organisationId) {
      setState('unavailable');
      setStudioName('');
      return 'unavailable';
    }

    setState('loading');
    try {
      const [membershipResponse, joinResponse, publicResponse] = await Promise.all([
        userApi.getOrganisations(),
        userApi.getJoinRequests(),
        organisationApi.getPublicList(),
      ]);
      // An organisation change, StrictMode replay, user action, or unmount may
      // have started a newer authority decision while these calls were in
      // flight. A stale load neither mutates UI state nor returns a decision
      // that its caller could treat as current.
      if (!isCurrent()) return 'stale';
      const expected = String(organisationId);
      const memberships = listFrom(membershipResponse, ['organisations']);
      const requests = listFrom(joinResponse, ['joinRequests']);
      const publicOrganisations = listFrom(publicResponse, ['organisations']);
      const membership = memberships.find((item) => organisationIdOf(item) === expected);
      const request = requests.find((item) => organisationIdOf(item) === expected);
      const directoryOrganisation = publicOrganisations.find((item) => organisationIdOf(item) === expected);
      const resolvedName = organisationNameOf(membership) || organisationNameOf(request) || organisationNameOf(directoryOrganisation);

      setStudioName(resolvedName);
      if (membership) {
        setState('member');
        return 'member';
      }
      if (String(request?.status || '').toLowerCase() === 'pending') {
        setState('pending');
        return 'pending';
      }
      // Never render the required named action with a guessed or projection-
      // supplied studio name. A missing directory record fails this optional
      // path closed without affecting the Sheet review.
      setState(resolvedName ? 'ready' : 'unavailable');
      return resolvedName ? 'ready' : 'unavailable';
    } catch {
      if (isCurrent()) {
        setState('unavailable');
        setStudioName('');
      }
      return isCurrent() ? 'unavailable' : 'stale';
    }
  }, [organisationId]);

  useEffect(() => {
    setDismissed(false);
    setMessage('');
    loadAuthority();
    return () => { authorityGenerationRef.current += 1; };
  }, [loadAuthority]);

  if (!organisationId) return null;

  const requestJoin = async () => {
    const generation = authorityGenerationRef.current + 1;
    authorityGenerationRef.current = generation;
    const isCurrent = () => mountedRef.current && authorityGenerationRef.current === generation;
    setState('requesting');
    setMessage('Sending your join request…');
    try {
      await userApi.requestJoin(Number(organisationId));
      if (!isCurrent()) return;
      setState('pending');
      setMessage('Your request was sent. Your Sheet Review access is unchanged while the studio decides.');
      focusStatus();
      // Refresh the independent authority projection after the mutation. The
      // successful response already proves pending, so a delayed read cannot
      // reopen the button and permit a duplicate request.
      const refreshed = await loadAuthority();
      if (mountedRef.current && refreshed === 'ready') setState('pending');
    } catch (cause) {
      if (!isCurrent()) return;
      if (Number(cause?.response?.status) === 409) {
        const refreshed = await loadAuthority();
        if (!mountedRef.current || refreshed === 'stale') return;
        if (!['pending', 'member'].includes(refreshed)) {
          setState('unavailable');
          setMessage('A join request already exists, but its current state could not be confirmed. Your Sheet Review access is unchanged.');
        } else {
          setMessage(refreshed === 'member'
            ? 'You are already a studio member. Your Sheet Review access remains independently controlled.'
            : 'Your request is already pending. Your Sheet Review access is unchanged while the studio decides.');
        }
      } else {
        setState('ready');
        setMessage('We could not send that join request. Your Sheet Review access is unchanged.');
      }
      focusStatus();
    }
  };

  const notNow = () => {
    setDismissed(true);
    setMessage('You can request studio access later. Your Sheet Review access is unchanged.');
    focusStatus();
  };

  return <section className="mc-review-join-choice" aria-labelledby="review-join-title">
    <h2 id="review-join-title">Want the full studio workspace?</h2>
    <p>Requesting to join may make future invitations and member features easier. Approval follows the studio’s normal process and is never required to review this Sheet.</p>
    <p className="mc-muted">Joining does not raise this Sheet’s Review access or add access to another Sheet.</p>
    {state === 'loading' && <p role="status" aria-live="polite">Loading studio membership…</p>}
    {state === 'pending' && !message && <p role="status">Your request is pending. Your Sheet Review access is unchanged while the studio decides.</p>}
    {state === 'member' && !message && <p role="status">You are already a studio member. Your Sheet Review access remains independently controlled.</p>}
    {state === 'unavailable' && !message && <p role="status">Studio membership options are unavailable right now. Your Sheet Review access is unchanged.</p>}
    {message && <p ref={statusRef} tabIndex="-1" role="status" aria-live="polite">{message}</p>}
    {state === 'ready' && !dismissed && <div className="mc-review-join-actions"><button type="button" className="mc-primary" onClick={requestJoin}>Request to join {studioName}</button><button type="button" className="mc-primary" onClick={notNow}>Not now</button></div>}
    {state === 'requesting' && <div className="mc-review-join-actions"><button type="button" className="mc-primary" disabled>Requesting…</button><button type="button" className="mc-primary" disabled>Not now</button></div>}
  </section>;
}

function publishedSnapshot(sheet = {}) {
  if (sheet.snapshot) return sheet.snapshot;
  const revisions = Array.isArray(sheet.revisions) ? sheet.revisions : [];
  const revision = revisions.find((item) => item.revisionId === sheet.publishedRevisionId)
    || revisions.find((item) => item.lifecycle === 'published')
    || revisions[0];
  return revision?.stateSnapshot || {};
}

function sourceFromSnapshot(snapshot = {}) {
  if (typeof snapshot.sourceDocument?.body === 'string' && snapshot.sourceDocument.body.trim()) return snapshot.sourceDocument.body;
  return (snapshot.sections || []).map((section) => {
    const blocks = (snapshot.blocks || []).filter((block) => block.sectionId === section.id).sort((a, b) => Number(a.startCount) - Number(b.startCount));
    const notes = String(section.practiceNote || '').split(/\r?\n/).filter(Boolean);
    return [`# ${section.label}`, ...blocks.map((block, index) => {
      const countLength = Math.max(1, Number(block.countLength) || 8);
      const token = countLength > 8 && countLength % 8 === 0 ? `[${countLength / 8}×8]` : `[${countLength}]`;
      return `${token}${notes[index] ? ` ${notes[index]}` : ''}`;
    })].join('\n');
  }).join('\n\n');
}

export default function ReviewerSheet() {
  const { resourceId } = useParams();
  const { user } = useAuth();
  const [review, setReview] = useState(null);
  const [error, setError] = useState('');
  const [reviewDisplay, setReviewDisplay] = useState('inline');
  const sourceRef = useRef(null);

  // !434 / MCRO001-F0015/F0018: only a transient failure (no response, or 5xx)
  // of the projection this page already loaded may keep the private Sheet and
  // the reviewer's in-progress draft mounted. Authorization/not-found
  // responses and an invalid or downgraded projection revoke it immediately.
  const loadedResourceRef = useRef(null);
  const load = useCallback(async () => {
    let response;
    try {
      response = await choreographyApi.getResourceReview(resourceId);
    } catch (cause) {
      const status = Number(cause?.response?.status);
      const transient = !cause?.response || (status >= 500 && status < 600);
      if (loadedResourceRef.current !== resourceId || !transient) {
        loadedResourceRef.current = null;
        setReview(null);
        setError(unavailable);
      }
      throw new Error(unavailable);
    }
    const context = payload(response).reviewContext;
    if (!context?.sheet || !context?.community || !['review', 'edit'].includes(context.permissionLevel)) {
      loadedResourceRef.current = null;
      setReview(null);
      setError(unavailable);
      throw new Error(unavailable);
    }
    loadedResourceRef.current = resourceId;
    setReview(context);
    setError('');
    return { data: { data: context.community } };
  }, [resourceId]);

  useEffect(() => { load().catch(() => undefined); }, [load]);
  const sheet = review?.sheet || {};
  const snapshot = useMemo(() => publishedSnapshot(sheet), [sheet]);
  const source = String(sheet.source || sourceFromSnapshot(snapshot));
  const orgId = review?.organisationId;
  useEffect(() => { setReviewDisplay(readCommunityReviewDisplay(user?.id || user?.userId, orgId, resourceId)); }, [orgId, resourceId, user?.id, user?.userId]);
  const changeReviewDisplay = (value) => { const next = normalizeCommunityReviewDisplay(value); setReviewDisplay(next); writeCommunityReviewDisplay(user?.id || user?.userId, orgId, resourceId, next); };

  if (error) return <main className="mc-shared-page"><section className="mc-panel mc-shared-card"><span className="mc-kicker">Choreo Sheet review</span><h1>Review unavailable</h1><p role="alert">{error}</p></section></main>;
  if (!review) return <main className="mc-shared-page"><section className="mc-panel mc-shared-card"><p role="status">Loading Sheet review…</p></section></main>;

  return <main className="mc-shared-page"><section className="mc-panel mc-reviewer-sheet" aria-labelledby="reviewer-sheet-title">
    <header className="mc-reviewer-sheet-head"><div><span className="mc-kicker">Choreo Sheet review</span><h1 id="reviewer-sheet-title">{sheet.title || review.resource?.label || 'Shared choreography Sheet'}</h1><p className="mc-muted">Review access · Sheet text is read-only</p></div><label className="mc-field mc-review-display-field"><span>Review</span><select aria-label="Review display" value={reviewDisplay} onChange={(event) => changeReviewDisplay(event.target.value)}>{COMMUNITY_REVIEW_DISPLAYS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label></header>
    <div className={`mc-community-canvas-layout mc-review-${reviewDisplay}-layout`}>
      <section aria-label="Published Sheet">
        {source ? <textarea ref={sourceRef} className="mc-reviewer-source" aria-label="Published choreography text" value={source} readOnly /> : <div className="mc-shared-sections">{(snapshot.sections || []).map((section) => <article key={section.id || section.label}><strong>{section.label}</strong>{section.practiceNote && <p>{section.practiceNote}</p>}</article>)}</div>}
      </section>
      <CommunityReviewLayer
        orgId={review.organisationId}
        routineId={review.resource?.id || resourceId}
        revisionId={review.community.revisionId || sheet.publishedRevisionId}
        source={source}
        sourceRef={sourceRef}
        loadCommunity={load}
        display={reviewDisplay}
      />
    </div>
    <OptionalStudioJoin organisationId={orgId} />
  </section></main>;
}
